import React, { ReactElement, useMemo, useState } from 'react';
import difference from 'lodash-es/difference';
import { t } from 'i18next';
import { Trans } from 'react-i18next';
import { Bill } from '@enview/interface/types/bills/Bill';
import ConfirmActionPopover from '../Popovers/ConfirmActionPopover';
import WorkspaceSelectorContent from '../DropDowns/WorkspaceSelectorContent';
import ArrowDown from '../../../components/svg/ArrowDown';
import Can from '../../../components/Can';
import GateTooltip, { GateTooltipFeature } from '../Tooltips/TooltipGate';
import { g as abilityGlossary } from '../../../config/ability';
import { BillAPI, TeamAPI } from '../../../api';
import { checkPermission, getOrgForTeamMode } from '../../../dux';
import { State, Thunk } from '../../../dux/@types';
import filteredTeamMembershipsForSelector from '../../../helpers/TeamHelper';
import useSearchId from '../../../hooks/useSearchId';
import { useDispatch, useSelector } from 'react-redux';
import './TrackColumnField.scss';

interface TrackColumnFieldProps {
  bill: Bill;
}

// The Tracking table-column cell: a TRACK/TRACKING pill plus a caret, both part of
// one clickable trigger that opens the shared WorkspaceSelectorContent (as "TRACK
// INTO...") to manage which of the user's workspaces the bill is tracked in.
const TrackColumnField = (props: TrackColumnFieldProps): ReactElement => {
  const { bill } = props;
  const dispatch = useDispatch();

  // bill.teams is the current user's own teams tracking this bill — distinct from
  // bill.teamsTracking/bill.workspaces, which cover every team in the org.
  const trackedTeamIds = (bill.teams ?? []).map((team) => team.id);
  const isTracked = trackedTeamIds.length > 0;

  const organization = useSelector(getOrgForTeamMode);
  const orgId = organization?.id;
  const orgUser = useSelector((state: State) => state.account.organizationUser);
  const { data: teamMemberships } =
    TeamAPI.endpoints.getUsersTeamMemberships.useQuery(undefined);
  const userWorkspaces = filteredTeamMembershipsForSelector(
    teamMemberships,
    orgUser,
    orgId,
  );

  const canTrack = (): Thunk<boolean> =>
    dispatch(checkPermission(abilityGlossary.TRACK, abilityGlossary.BILL, true));
  const teamMembershipIds = (teamMemberships ?? []).map((team) => team.teamId);
  const { data: trackingLimit } = BillAPI.endpoints.getTrackedBillsLimit.useQuery(
    teamMembershipIds,
    { skip: !canTrack() || teamMembershipIds.length === 0 },
  );
  // Mirrors BillTrackingButton's limit check — prevents firing a trackBill request
  // the backend would reject anyway (bills.ts enforces this limit server-side).
  const canTrackMoreBills = useMemo<boolean>(() => {
    if (trackingLimit?.limit && trackingLimit.bills.length >= trackingLimit.limit) {
      return false;
    }
    return true;
  }, [trackingLimit]);

  const { searchId, page, pageSize } = useSearchId();
  const [trackBill] = BillAPI.endpoints.trackBill.useMutation();
  const [untrackBill] = BillAPI.endpoints.untrackBill.useMutation();

  const [show, setShow] = useState(false);

  const handleToggle = (): void => {
    setShow((prevShow) => !prevShow);
  };

  const handleClose = (): void => {
    setShow(false);
  };

  const handleSave = (teamIds: number[]): void => {
    handleClose();
    const newTeamsToTrack = difference(teamIds, trackedTeamIds);
    const oldTeamsToUntrack = difference(trackedTeamIds, teamIds);
    // Adding a bill past the org's tracking limit is rejected server-side (403) —
    // skip the doomed request rather than firing it and swallowing the failure.
    if (newTeamsToTrack.length && canTrackMoreBills) {
      void trackBill({ bill, teamIds: newTeamsToTrack, searchId, page, pageSize });
    }
    if (oldTeamsToUntrack.length) {
      void untrackBill({ bill, teamIds: oldTeamsToUntrack });
    }
  };

  const trigger = (
    <span className="tcf-trigger">
      <span
        className={`tcf-pill ${isTracked ? 'tcf-pill-tracking' : 'tcf-pill-track'}`}
      >
        <span className="tcf-dot" />
        <span className="tcf-label">{isTracked ? 'TRACKING' : 'TRACK'}</span>
      </span>
      <ArrowDown className="tcf-caret" />
    </span>
  );

  return (
    <Can I={abilityGlossary.TRACK} a={abilityGlossary.BILL} passThrough>
      {(can: boolean) => (
        <span className="tcf-wrapper" id={`track-column-${bill.id}`}>
          <ConfirmActionPopover
            align="start"
            className="workspace-picker-popover"
            closePopup={handleClose}
            content={
              show ? (
                <WorkspaceSelectorContent
                  billWorkspaceIds={trackedTeamIds}
                  onSave={handleSave}
                  sectionLabel={
                    canTrackMoreBills
                      ? 'TRACK INTO...'
                      : 'TRACK INTO... (bill limit reached)'
                  }
                  workspaces={userWorkspaces}
                />
              ) : null
            }
            hideCloseIcon
            onClickOutside={handleClose}
            positions={['bottom', 'top', 'right', 'left']}
            show={can && show}
            showArrowContainer={false}
            target={trigger}
            toggleCallback={can ? handleToggle : () => {}}
          />
          {!can && (
            <GateTooltip
              accountType="free"
              anchorId={`track-column-${bill.id}`}
              customContent={
                <Trans
                  components={{
                    trackLink: <a href={t('urls.createAccount')} target="_blank" />,
                  }}
                  i18nKey="featureGating.tooltipText.trackButton"
                  key="featureGating.tooltipText.trackButton"
                />
              }
              featureName={GateTooltipFeature.BillTracking}
              place="left"
            />
          )}
        </span>
      )}
    </Can>
  );
};

export default TrackColumnField;
